import React, { useState, PropsWithChildren } from 'react';
import { css } from '@emotion/react';
import MuiMenu from '@mui/icons-material/Menu';
import { i18n_sign_in } from '@yi/i18n/phrases/i18n_sign_in';
import { i18n_start_free_trial } from '@yi/i18n/phrases/i18n_start_free_trial';

import { useHeaderGlobalSearchButtonVisibility } from '~/src/common/appearence/header';
import { useShowSaleBanner } from '~/src/common/courses/sale/useShowSaleBanner';
import { useIsGreaterThanTablet } from '~/src/common/mediaQueries';
import { useHomePathname } from '~/src/common/navigation';
import { useCurrentUser } from '~/src/common/user/useCurrentUser';
import { Button } from '~/src/components/buttons';
import { CurrentUserAvatar } from '~/src/components/CurrentUserAvatar';
import { useHasMounted } from '~/src/components/hooks/useHasMounted';
import { useIsSSR } from '~/src/components/hooks/useIsSSR';
import { IconLogo, IconLogoText } from '~/src/components/icons';
import { useLocale } from '~/src/components/LocaleProvider';
import { LocalizedLink } from '~/src/components/LocalizedLink';
import { SignInModal } from '~/src/components/modals';
import { breakpoints, fontWeight } from '~/styles';

import { NavList } from './elements';
import { ReactComponent as IconSearch } from './icon-search.svg';

interface Props {
  toggleSideNav(event: React.KeyboardEvent | React.MouseEvent): void;
  hideSideNav(): void;
  toggleUserMenu(): void;
}

enum GridArea {
  menuBtn = 'menuBtn',
  logo = 'logo',
  btnGroup = 'btnGroup',
}

const styles = {
  startTrialBtn: css({
    fontFamily: 'Montserrat',
    color: 'var(--Pure-White, #FFF)',
    padding: '10px 16px',
    fontSize: '14px',
    lineHeight: '16px',
    letterSpacing: '0.7px',
    height: 'auto',
    fontWeight: fontWeight.bold,
    [breakpoints.minTablet]: {
      padding: '0px 26px',
      height: '56px',
      fontSize: '15px',
      lineHeight: 'normal',
      letterSpacing: '0.05em',
    },
    [breakpoints.minDesktop]: {
      fontSize: '16px',
      letterSpacing: '0.8px',
    },
  }),
  container: css({
    display: 'grid',
    alignItems: 'center',
    gridTemplateAreas: `'${GridArea.menuBtn} . ${GridArea.logo} . ${GridArea.btnGroup}'`,
    gridTemplateColumns: `auto 16px auto 1fr auto`,
    [breakpoints.minTablet]: {
      gridTemplateAreas: `'${GridArea.menuBtn} ${GridArea.logo} ${GridArea.btnGroup}'`,
      gridTemplateColumns: `1fr auto 1fr`,
    },
  }),
  containerForAuthorizedUser: css({
    gridTemplateAreas: `'${GridArea.menuBtn} ${GridArea.logo} ${GridArea.btnGroup}'`,
    gridTemplateColumns: `1fr auto 1fr`,
  }),
  btnGroup: css({
    gridArea: GridArea.btnGroup,
    alignItems: 'center',
    gap: '8px',
    [breakpoints.minTablet]: {
      gap: '20px',
    },
  }),

  logoContainer: css({
    gridArea: GridArea.logo,
    display: 'flex',
    alignItems: 'center',
    [breakpoints.minTablet]: {
      gap: '8px',
    },
    [breakpoints.minDesktop]: {
      gap: '10px',
    },
  }),
  logo: css({
    height: '25px',
    width: 'auto',
    [breakpoints.minTablet]: {
      height: '40px',
    },
    [breakpoints.minDesktop]: {
      height: '30px',
    },
    [breakpoints.minDesktopLg]: {
      height: '36px',
    },
  }),
  logoForAuthorizedUser: css({
    height: '40px',
    width: 'auto',
    [breakpoints.minTablet]: {
      height: '28px',
    },
    [breakpoints.minDesktop]: {
      height: '36px',
    },
  }),
  yiLogoText: css({
    display: 'none',
    [breakpoints.minDesktop]: {
      display: 'block',
      height: '18px',
    },
    [breakpoints.minDesktopLg]: {
      height: '25px',
    },
  }),
  yiLogoTextForAuthorizedUser: css({
    display: 'none',
    [breakpoints.minTablet]: {
      display: 'block',
      height: '18px',
    },
  }),
  menuBtn: css({
    gridArea: GridArea.menuBtn,
    color: 'rgba(0, 0, 0, 0.54)',
    padding: 0,
    display: 'flex',
    justifySelf: 'start',
  }),
  search: {
    display: 'none',
    [breakpoints.minTabletLandscape]: {
      display: 'block',
    },
  },
  searchBtn: css({
    padding: 0,
    color: 'rgba(0, 0, 0, 0.54)',
    display: 'flex',
    alignItems: 'center',
    svg: {
      height: '21px',
      width: 'auto',
      [breakpoints.minDesktop]: {
        height: '30px',
      },
    },
  }),
  loggedOutBtnGroup: css({
    display: 'flex',
  }),
  userAvatar: css({
    top: '-3px !important',
    left: '-3px !important',
  }),
  userAvatarBtnWrapper: css({
    display: 'flex',
    alignItems: 'center',
    [breakpoints.minDesktop]: {
      paddingRight: '3px',
    },
  }),
  signInBtn: css({
    fontWeight: fontWeight.medium,
    fontSize: '14px',
    [breakpoints.minTablet]: {
      fontSize: '15px',
      lineHeight: 'normal',
      letterSpacing: '0.05em',
    },
    [breakpoints.minDesktop]: {
      fontSize: '16px',
      letterSpacing: '0.8px',
    },
  }),
};

const urlPathnameSignCTAShownBlacklist = [/^\/?(es\/)?membership\/?/i];

function useIsSignCTAShown() {
  const isSSR = useIsSSR();
  const { isShown } = useShowSaleBanner();
  if (isSSR) {
    return false;
  }

  const isInBlackList = urlPathnameSignCTAShownBlacklist.some(re => re.test(location.pathname));

  return !isInBlackList && !isShown;
}

export const DefaultNav: React.FC<PropsWithChildren<Props>> = ({
  toggleSideNav,
  toggleUserMenu,
  hideSideNav,
}) => {
  const { locale } = useLocale();
  const homePathname = useHomePathname();
  const { isLoggedIn, loading } = useCurrentUser();
  const [showSignInModal, setShowSignInModal] = useState(false);

  const { hasMounted } = useHasMounted();
  const isFirstRender = !hasMounted;

  const userStatus =
    loading || isFirstRender ? 'loading' : isLoggedIn ? 'authorized' : 'unauthorized';

  const isGreaterThanTablet = useIsGreaterThanTablet();
  const avatarWidth = isGreaterThanTablet ? 32 : 26;

  const { isShown: isGlobalSearchIconShown } = useHeaderGlobalSearchButtonVisibility();
  const isSignCTAShown = useIsSignCTAShown();

  return (
    <div
      css={[
        styles.container,
        userStatus === 'authorized' ? styles.containerForAuthorizedUser : null,
      ]}
    >
      <Button
        aria-label="Toggle side navigation"
        onClick={toggleSideNav}
        data-testid="nav-side-nav-button"
        css={styles.menuBtn}
        kind="unstyled"
      >
        <MuiMenu style={{ height: 28, width: 28 }} />
      </Button>
      <LocalizedLink aria-label="Logo" to={homePathname} css={styles.logoContainer}>
        <IconLogo css={userStatus === 'authorized' ? styles.logoForAuthorizedUser : styles.logo} />
        <IconLogoText
          css={userStatus === 'authorized' ? styles.yiLogoTextForAuthorizedUser : styles.yiLogoText}
        />
      </LocalizedLink>
      <NavList aria-label="Sign in and search box" locale={locale} css={styles.btnGroup}>
        {isGlobalSearchIconShown && (
          <LocalizedLink to="/search" css={styles.search}>
            <Button aria-label="Search" css={styles.searchBtn} kind="unstyled">
              <IconSearch />
            </Button>
          </LocalizedLink>
        )}
        {userStatus === 'authorized' && (
          <Button
            onClick={toggleUserMenu}
            data-testid="nav-user-menu-button"
            aria-label="User menu"
            kind="unstyled"
            containerCss={styles.userAvatarBtnWrapper}
          >
            <CurrentUserAvatar width={avatarWidth} css={styles.userAvatar} />
          </Button>
        )}
        {userStatus === 'unauthorized' && (
          <>
            <Button
              onClick={() => {
                setShowSignInModal(true);
                hideSideNav();
              }}
              data-testid="signin-open-modal"
              aria-label="Sign In"
              kind="text"
              css={styles.signInBtn}
            >
              {i18n_sign_in(locale)}
            </Button>
            {isSignCTAShown && (
              <LocalizedLink to="/membership" style={{ textDecoration: 'none' }}>
                <Button kind="primary" aria-label="Start free trial" css={styles.startTrialBtn}>
                  {i18n_start_free_trial(locale)}
                </Button>
              </LocalizedLink>
            )}
            <SignInModal open={showSignInModal} setOpen={setShowSignInModal} />
          </>
        )}
      </NavList>
    </div>
  );
};
